import type { TFunction } from 'i18next';

import type { UpgradePreviewPricingPolicy } from '../types/portalUpgradePreview';
import { formatPortalDate } from './portalOverviewHelpers';
import { isApiError } from '@/shared/lib/apiClient';
import { formatMoney } from '@/shared/utils/money';

const UPGRADE_PREVIEW_EMPTY_VALUE = '—';

export function formatUpgradePreviewRemainingRatio(ratio: number, locale: string): string {
  return new Intl.NumberFormat(locale.startsWith('ar') ? 'ar-SA' : 'en-US', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(ratio);
}

export function formatUpgradePreviewMoney(
  amountMinorUnits: number,
  currency: string,
  locale: string,
): string {
  const language = locale.startsWith('ar') ? 'ar' : 'en';
  return formatMoney(amountMinorUnits, currency, language);
}

export function formatUpgradePreviewDate(iso: string | null | undefined, locale: string): string {
  return formatPortalDate(iso ?? null, locale);
}

export function formatUpgradePreviewOriginalPrice(
  amountMinorUnits: number | null,
  currency: string,
  locale: string,
): string {
  if (amountMinorUnits === null) return UPGRADE_PREVIEW_EMPTY_VALUE;
  return formatUpgradePreviewMoney(amountMinorUnits, currency, locale);
}

export function formatUpgradePreviewDiscountPercent(
  discountPercent: number,
  locale: string,
): string {
  return new Intl.NumberFormat(locale.startsWith('ar') ? 'ar-SA' : 'en-US', {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(discountPercent / 100);
}

export function resolveUpgradePreviewExpiredLabel(isExpired: boolean, t: TFunction): string {
  return t(
    isExpired
      ? 'portal.upgradePreview.subscriptionPeriod.expired'
      : 'portal.upgradePreview.subscriptionPeriod.active',
  );
}

export function resolveUpgradePreviewPricingPolicyKey(policy: UpgradePreviewPricingPolicy): string {
  return `portal.upgradePreview.pricingPolicy.${policy}`;
}

export function resolveUpgradePreviewErrorMessage(error: unknown, t: TFunction): string {
  if (isApiError(error)) {
    if (error.code === 'UPGRADE_BILLING_CYCLE_MISMATCH') {
      return t('portal.upgradePreview.errors.billingCycleMismatch');
    }
    if (error.message.trim().length > 0) {
      return error.message;
    }
  }
  return t('portal.upgradePreview.errors.generic');
}
